import {v4 as uuidv4} from 'uuid';
import {z} from 'zod';

import {timeFormat} from '@/components/TaskTimeInput';
import i18n from '@/i18n';
import {
  type CreateResidentMedicationInput,
  type DosageDetails,
  type DosageDetailsInput,
  type MedicationFoodRelation,
  MedicationForm,
  type PartOfTheDay,
  type ResidentMedication,
  TimeType,
  TreatmentType,
  UnitType,
  type UpdateResidentMedicationInput,
} from '@/services/graphqlHooks';

import type {DosageDetailsWithId} from './medicationTypes';

export const FORM_SUGGESTED_UNITS: Partial<Record<MedicationForm, UnitType[]>> =
  {
    [MedicationForm.Tablet]: [UnitType.Tablet],
    [MedicationForm.Capsule]: [UnitType.Capsule],
    [MedicationForm.Injection]: [
      UnitType.Ml,
      UnitType.Ampoule,
      UnitType.Injection,
    ],
    [MedicationForm.Syrup]: [UnitType.Ml],
    [MedicationForm.Ointment]: [UnitType.G, UnitType.Application],
    [MedicationForm.Drops]: [UnitType.Drop, UnitType.Ml, UnitType.Application],
    [MedicationForm.Spray]: [UnitType.Spray, UnitType.Puff],
    [MedicationForm.Plaster]: [UnitType.Patch],
    [MedicationForm.TransdermalSystem]: [UnitType.Patch],
    [MedicationForm.Inhalation]: [UnitType.Puff, UnitType.Ml],
    [MedicationForm.Aerosol]: [UnitType.Puff, UnitType.Spray, UnitType.Ml],
  };

export enum MedicationScheduleMode {
  Daily = 'daily',
  SpecificDays = 'specificDays',
}

/***********************
 * Medication Basic Inputs
 ***********************/
const requiredPositiveQuantityString = z.preprocess(
  value =>
    value === '' || value == null
      ? undefined
      : typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : undefined,
  z
    .string({
      error: issue =>
        issue.input === undefined ? i18n.t('Required') : undefined,
    })
    .refine(val => /^[\d,./]+$/.test(val), i18n.t('Invalid quantity'))
    .refine(
      val => (val.match(/[,./]/g) ?? []).length <= 1,
      i18n.t('Invalid quantity')
    )
    .refine(val => {
      if (val.includes('/')) {
        const parts = val.split('/');
        if (parts.length !== 2) return false;
        const [num, den] = parts.map(p => parseFloat(p.replace(',', '.')));
        return !isNaN(num) && !isNaN(den) && den !== 0 && num / den > 0;
      }
      const parsed = parseFloat(val.replace(',', '.'));
      return !isNaN(parsed) && parsed > 0;
    }, i18n.t('Must be a positive number'))
) as z.ZodType<string, string>;

export const residentMedicationBasicFormSchema = z
  .object({
    treatmentType: z.string().min(1, i18n.t('Required')),
    medicationName: z.string().trim().min(1, i18n.t('Name is required')),
    notes: z.string().optional(),
    residentId: z.string().trim().min(1, i18n.t('Resident ID is required')),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    productId: z.string().optional(),
    scheduleMode: z
      .enum([MedicationScheduleMode.Daily, MedicationScheduleMode.SpecificDays])
      .optional(),
    daysOfWeek: z.array(z.number()).optional(),
    dosageDetails: z.array(z.any()),
    form: z.string().min(1, i18n.t('Required')),
    warnings: z.string().optional(),
    showForDistribution: z.boolean(),
    asNeededQuantity: requiredPositiveQuantityString.optional(),
    asNeededUnit: z.string().optional(),
  })
  .superRefine((values, context) => {
    const isAsNeeded =
      (values.treatmentType as TreatmentType) === TreatmentType.AsNeeded;

    if (isAsNeeded && values.asNeededQuantity == null) {
      context.addIssue({
        code: 'custom',
        message: i18n.t('Required'),
        path: ['asNeededQuantity'],
      });
    }

    if (isAsNeeded && !values.asNeededUnit) {
      context.addIssue({
        code: 'custom',
        message: i18n.t('Required'),
        path: ['asNeededUnit'],
      });
    }

    if (!isAsNeeded && values.dosageDetails.length === 0) {
      context.addIssue({
        code: 'custom',
        message: i18n.t('Required'),
        path: ['dosageDetails'],
      });
    }

    if (
      !isAsNeeded &&
      values.scheduleMode === MedicationScheduleMode.SpecificDays &&
      !values.daysOfWeek?.length
    ) {
      context.addIssue({
        code: 'custom',
        message: i18n.t('Required'),
        path: ['daysOfWeek'],
      });
    }
  });

export type ResidentMedicationBasicFormValues = z.infer<
  typeof residentMedicationBasicFormSchema
>;

export const residentMedicationBasicFormDefaultValues = (
  value?: Partial<ResidentMedication>
): ResidentMedicationBasicFormValues => {
  const isAsNeeded = value?.treatmentType === TreatmentType.AsNeeded;
  const firstDosage = value?.schedule?.dosageDetails?.[0];
  const daysOfWeek = (value?.schedule?.daysOfWeek || []) as number[];
  return {
    treatmentType:
      (value?.treatmentType as TreatmentType) ?? TreatmentType.Continuous,
    medicationName: value?.medicationName || '',
    notes: value?.notes || '',
    residentId: value?.residentId || '',
    startDate: value?.startDate || '',
    endDate: value?.endDate || '',
    productId: value?.productId ?? '',
    scheduleMode: daysOfWeek.length
      ? MedicationScheduleMode.SpecificDays
      : MedicationScheduleMode.Daily,
    daysOfWeek,
    dosageDetails: isAsNeeded
      ? []
      : (value?.schedule?.dosageDetails || []).map(dosage => ({
          ...dosage,
          relationToFood: dosage?.relationToFood ?? null,
          id: uuidv4(),
        })),
    form: (value?.form as MedicationForm) ?? '',
    warnings: value?.warnings || '',
    showForDistribution: value?.showForDistribution ?? true,
    asNeededQuantity:
      isAsNeeded && firstDosage?.dosage?.quantity != null
        ? String(firstDosage.dosage.quantity)
        : undefined,
    asNeededUnit: isAsNeeded ? firstDosage?.dosage?.unit ?? '' : '',
  };
};

const mapRelationToFood = (
  relationToFood?: MedicationFoodRelation | 'null' | null
): MedicationFoodRelation | null =>
  relationToFood && relationToFood !== 'null' ? relationToFood : null;

const mapScheduledDosageDetails = (
  dosageDetails: DosageDetailsWithId[] = []
): DosageDetailsInput[] =>
  dosageDetails.map(
    ({
      customInstructions,
      dosage,
      relationToFood,
      time,
      timeOfDay,
      timeType,
    }) => ({
      customInstructions,
      dosage: {
        quantity: dosage.quantity,
        unit: dosage.unit,
      },
      relationToFood: mapRelationToFood(relationToFood),
      time,
      timeOfDay,
      timeType,
    })
  );

const mapAsNeededDosageDetails = (
  values: ResidentMedicationBasicFormValues
): DosageDetailsInput[] => {
  if (values.asNeededQuantity == null || !values.asNeededUnit) {
    throw new Error('As needed medication requires quantity and unit.');
  }

  return [
    {
      timeType: TimeType.PartOfDay,
      time: null,
      timeOfDay: null,
      dosage: {
        quantity: values.asNeededQuantity,
        unit: values.asNeededUnit as UnitType,
      },
      relationToFood: null,
      customInstructions: null,
    },
  ];
};

export const mapResidentMedicationBasicFormValuesToUpdateResidentMedicationInput =
  (
    values: ResidentMedicationBasicFormValues,
    residentMedication?: Partial<ResidentMedication>
  ): UpdateResidentMedicationInput => {
    const isAsNeeded =
      (values.treatmentType as TreatmentType) === TreatmentType.AsNeeded;
    const daysOfWeek =
      values.scheduleMode === MedicationScheduleMode.SpecificDays
        ? values.daysOfWeek || []
        : [];

    return {
      ...(residentMedication || {}),
      id: residentMedication?.id || '',
      medicationName: values?.medicationName || '',
      notes: values?.notes,
      productId: values?.productId || undefined,
      startDate: isAsNeeded ? null : values?.startDate || null,
      endDate: isAsNeeded ? null : values?.endDate || null,
      treatmentType: (values?.treatmentType as TreatmentType) || undefined,
      form: (values?.form as MedicationForm) || undefined,
      warnings: values?.warnings || undefined,
      showForDistribution: values.showForDistribution,
      schedule: {
        ...(residentMedication?.schedule || {}),
        daysOfWeek: isAsNeeded ? [] : daysOfWeek,
        dosageDetails: isAsNeeded
          ? mapAsNeededDosageDetails(values)
          : mapScheduledDosageDetails(
              values?.dosageDetails as DosageDetailsWithId[]
            ),
      },
    };
  };

export const mapResidentMedicationBasicFormValuesToCreateResidentMedicationInput =
  (
    values: ResidentMedicationBasicFormValues,
    residentMedication?: Partial<ResidentMedication>
  ): CreateResidentMedicationInput => {
    const isAsNeeded =
      (values.treatmentType as TreatmentType) === TreatmentType.AsNeeded;
    const daysOfWeek =
      values.scheduleMode === MedicationScheduleMode.SpecificDays
        ? values.daysOfWeek || []
        : [];

    return {
      ...(residentMedication || {}),
      medicationName: values?.medicationName || '',
      productId: values?.productId || undefined,
      notes: values?.notes,
      residentId: values?.residentId,
      startDate: isAsNeeded ? undefined : values?.startDate || undefined,
      endDate: isAsNeeded ? undefined : values?.endDate || undefined,
      treatmentType: (values?.treatmentType as TreatmentType) || undefined,
      form: (values?.form as MedicationForm) || undefined,
      warnings: values?.warnings || undefined,
      showForDistribution: values.showForDistribution,
      schedule: {
        ...(residentMedication?.schedule || {}),
        daysOfWeek: isAsNeeded ? [] : daysOfWeek,
        dosageDetails: isAsNeeded
          ? mapAsNeededDosageDetails(values)
          : mapScheduledDosageDetails(
              values?.dosageDetails as DosageDetailsWithId[]
            ),
      },
    };
  };

/***********************
 * Medication Dosage Inputs
 ***********************/

export const residentMedicationDosageFormSchema = z.object({
  time: z.string().trim().min(1, i18n.t('Time is required')),
  dosageQuantity: requiredPositiveQuantityString,
  dosageUnit: z.string().min(1, i18n.t('Required')),
  relationToFood: z.string().nullable().optional(),
  customInstructions: z.string().optional(),
});

export type ResidentMedicationDosageFormValues = z.infer<
  typeof residentMedicationDosageFormSchema
>;

const getTimeAndTimeOfDay = (
  time: string
): {
  time: string | null;
  timeType: TimeType;
  timeOfDay: PartOfTheDay | null;
} => {
  const isTimeFormat = timeFormat.test(time);

  return isTimeFormat
    ? {
        time,
        timeType: TimeType.Specific,
        timeOfDay: null,
      }
    : {
        timeOfDay: time as PartOfTheDay,
        timeType: TimeType.PartOfDay,
        time: null,
      };
};

export const residentMedicationDosageFormDefaultValues = (
  dosageDetails?: DosageDetails,
  suggestedUnit?: UnitType
) => ({
  time: dosageDetails?.time ?? dosageDetails?.timeOfDay ?? '',
  dosageQuantity:
    dosageDetails?.dosage?.quantity != null
      ? String(dosageDetails.dosage.quantity)
      : undefined,
  dosageUnit: dosageDetails?.dosage?.unit ?? suggestedUnit ?? '',
  relationToFood: dosageDetails?.relationToFood ?? '',
  customInstructions: dosageDetails?.customInstructions ?? '',
});

export const mapResidentMedicationDosageFormValuesToDosageInput = (
  values: ResidentMedicationDosageFormValues
): DosageDetails => ({
  ...getTimeAndTimeOfDay(values.time),
  dosage: {
    quantity: values.dosageQuantity,
    unit: values.dosageUnit as UnitType,
  },
  relationToFood: values.relationToFood
    ? (values.relationToFood as MedicationFoodRelation)
    : null,
  customInstructions: values.customInstructions,
});
